@use "../../styles/colors";

$logomark-background-color-variable-name: --ixf-logomark-background;

.logomark {
  // Default size: medium
  --ixf-logomark-size: 24px;
  --ixf-logomark-image-size: 100%;
  --ixf-logomark-border-radius: 6px;
  --ixf-logomark-font-size: 0.75rem;
  --ixf-logomark-line-height: 0.75rem;

  // Default variant: primary
  --ixf-logomark-font-color: var(--ixf-color-primary-dk);
  --ixf-logomark-border: none;
  --ixf-logomark-box-shadow: none;

  height: var(--ixf-logomark-size);
  width: var(--ixf-logomark-size);
  border-radius: var(--ixf-logomark-border-radius);
  color: var(--ixf-logomark-font-color);
  font-size: var(--ixf-logomark-font-size);
  line-height: var(--ixf-logomark-line-height);
  font-weight: var(--ixf-font-weight-main-medium);
  background: var(#{$logomark-background-color-variable-name});
  border: var(--ixf-logomark-border);
  box-shadow: var(--ixf-logomark-box-shadow);
  position: relative;
  overflow: hidden;

  &::before {
    box-shadow: var(--ixf-logomark-box-shadow);
    content: "";
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: var(--ixf-logomark-border-radius);
    z-index: 2;
  }

  .interchangeLogomark {
    height: unset !important; // this is set by the height property directly based on selected size

    &.white {
      color: var(--ixf-color-white);
    }
  }

  @include colors.define-best-supported-color-value(
    $logomark-background-color-variable-name,
    color(from var(--ixf-color-primary-md) srgb r g b / 0.2),
    color(from var(--ixf-color-primary-md) display-p3 r g b / 0.2)
  );

  > * {
    z-index: 1;
  }

  img {
    height: var(--ixf-logomark-image-size);
    width: var(--ixf-logomark-image-size);
  }

  &.size {
    &-small {
      --ixf-logomark-size: 16px;
      /* --ixf-logomark-image-size: 12px; */
      --ixf-logomark-border-radius: 4px;
      --ixf-logomark-font-size: 0.625rem;
      --ixf-logomark-line-height: 0.75rem;
    }

    &-large {
      --ixf-logomark-size: 32px;
      /* --ixf-logomark-image-size: 24px; */
      --ixf-logomark-font-size: 1.125rem;
      --ixf-logomark-line-height: 1.125rem;
    }
  }

  &.variant {
    &-secondary {
      --ixf-logomark-font-color: var(--ixf-color-secondary-dk);

      @include colors.define-best-supported-color-value(
        $logomark-background-color-variable-name,
        color(from var(--ixf-color-secondary-md) srgb r g b / 0.2),
        color(from var(--ixf-color-secondary-md) display-p3 r g b / 0.2)
      );
    }

    &-tertiary {
      --ixf-logomark-font-color: var(--ixf-color-tertiary-dk);

      @include colors.define-best-supported-color-value(
        $logomark-background-color-variable-name,
        color(from var(--ixf-color-tertiary-md) srgb r g b / 0.2),
        color(from var(--ixf-color-tertiary-md) display-p3 r g b / 0.2)
      );
    }

    &-quaternary {
      --ixf-logomark-font-color: var(--ixf-color-quaternary-dk);

      @include colors.define-best-supported-color-value(
        $logomark-background-color-variable-name,
        color(from var(--ixf-color-quaternary-md) srgb r g b / 0.2),
        color(from var(--ixf-color-quaternary-md) display-p3 r g b / 0.2)
      );
    }

    &-rainbow {
      #{$logomark-background-color-variable-name}: linear-gradient(
        135deg,
        var(--ixf-color-primary-md) 14.84%,
        var(--ixf-color-tertiary-md, #4390e5) 32.5%,
        var(--ixf-color-secondary-md, #c416fc) 50%,
        var(--ixf-color-quaternary-md, #e800bb) 67.5%,
        var(--ixf-color-danger-md, #ff8795) 85.5%
      );

      --ixf-logomark-font-color: var(--ixf-color-white);

      --ixf-logomark-box-shadow: 0 1px 3px 0
        var(--ixf-logomark-box-shadow-color) inset;

      @include colors.define-best-supported-color-value(
        --ixf-logomark-box-shadow-color,
        rgba(255, 255, 255, 0.5),
        color(display-p3 1 1 1 / 0.5)
      );
    }

    &-rainbow-icon {
      #{$logomark-background-color-variable-name}: linear-gradient(
        135deg,
        var(--ixf-color-background) 0%,
        var(--ixf-color-neutral-xlt) 100%
      );

      --ixf-logomark-box-shadow: 0 0 0 1px var(--ixf-color-separator);
    }

    &-image {
      #{$logomark-background-color-variable-name}: var(--ixf-color-white);
      --ixf-logomark-box-shadow: 0 0 0 1px var(--ixf-color-separator);
      --ixf-logomark-font-color: var(--ixf-text-tone-md);
    }
  }

  &.size-small.variant-rainbow {
    --ixf-logomark-box-shadow: 0 1px 2px 0
      var(--ixf-logomark-box-shadow-color) inset;
  }

  &.size-large.variant-rainbow {
    --ixf-logomark-box-shadow: 0 2px 4px 0
      var(--ixf-logomark-box-shadow-color) inset;
  }

  &.size-small.variant-image, &.size-small.variant-rainbow-icon {
    --ixf-logomark-box-shadow: none;
    
    &::before {
      --ixf-logomark-box-shadow: 0 0 0 1px var(--ixf-color-separator) inset;
    }
  }

}
